/**
 * Modal de configuração do node de verificação de gatilho
 * Permite selecionar quais triggers o node deve verificar
 * 
 * RELEASE: 1.0.0 - 2024-12-25 - Novo componente
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Target, 
  MessageSquare, 
  Clock, 
  Tag, 
  Columns, 
  Building2, 
  UserCheck,
  Webhook,
  Calendar,
  Zap,
  Filter
} from 'lucide-react';
import { FlowNode, TriggerType, TriggerCheckConfig } from '@/types/flows';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

/**
 * Configuração de cada trigger com label, ícone e descrição
 * 
 * RELEASE: 1.1.0 - 2024-12-26 - Adicionado 'any_message'
 * RELEASE: 1.2.0 - 2026-01-25 - Adicionado 'crm_filter' (Filtro Avançado CRM)
 */
const triggerOptions: Array<{
  type: TriggerType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  {
    type: 'first_message',
    label: 'Primeira mensagem',
    icon: MessageSquare,
    description: 'Primeira mensagem de um contato novo',
  },
  {
    type: 'active_conversation',
    label: 'Conversa em andamento',
    icon: UserCheck,
    description: 'Conversa já ativa com um agente',
  },
  {
    type: 'keyword',
    label: 'Palavra-chave',
    icon: Tag,
    description: 'Mensagem contém palavra específica',
  },
  {
    type: 'scheduled',
    label: 'Agendado',
    icon: Calendar,
    description: 'Execução programada por horário',
  },
  {
    type: 'inactivity',
    label: 'Inatividade',
    icon: Clock,
    description: 'Após período sem resposta',
  },
  {
    type: 'event',
    label: 'Evento',
    icon: Zap,
    description: 'Evento do sistema',
  },
  {
    type: 'webhook',
    label: 'Webhook',
    icon: Webhook,
    description: 'Chamada via webhook externo',
  },
  {
    type: 'contact_tag',
    label: 'Tag do contato',
    icon: Tag,
    description: 'Contato recebeu/possui tag',
  },
  {
    type: 'phase',
    label: 'Fase',
    icon: Columns,
    description: 'Contato em determinada fase',
  },
  {
    type: 'department',
    label: 'Departamento',
    icon: Building2,
    description: 'Conversa atribuída a departamento',
  },
  {
    type: 'any_message',
    label: 'Qualquer mensagem',
    icon: MessageSquare,
    description: 'Fallback para mensagens não categorizadas',
  },
  {
    type: 'crm_filter',
    label: 'Filtro Avançado CRM',
    icon: Filter,
    description: 'Negócio/conversa atende critérios CRM (retorna entity_id)',
  },
  {
    type: 'contract_event',
    label: 'Evento de Contrato',
    icon: Filter,
    description: 'Evento do ciclo de vida de contrato digital (assinatura, recusa, etc.)',
  },
];

interface ConfigureTriggerCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: TriggerCheckConfig) => void;
}

export function ConfigureTriggerCheckDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureTriggerCheckDialogProps) {
  const config = (node.data.config || {}) as TriggerCheckConfig;
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estado local para triggers selecionados
  const [selectedTriggers, setSelectedTriggers] = useState<TriggerType[]>(
    config.triggers_to_check || []
  );

  // Sincroniza estado quando dialog abre ou node muda
  useEffect(() => {
    if (open) {
      setSelectedTriggers((node.data.config as TriggerCheckConfig)?.triggers_to_check || []);
    }
  }, [open, node]);

  /**
   * Toggle seleção de trigger
   */
  const handleToggleTrigger = (trigger: TriggerType) => {
    if (selectedTriggers.includes(trigger)) {
      setSelectedTriggers(selectedTriggers.filter(t => t !== trigger));
    } else {
      setSelectedTriggers([...selectedTriggers, trigger]);
    }
  };

  /**
   * Salvar configuração
   */
  const handleSave = () => {
    onSave(node.id, {
      triggers_to_check: selectedTriggers,
      nickname: nicknameRef.current?.getNickname(),
    } as any);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-500" />
            Configurar Verificação de Gatilho
          </DialogTitle>
          <DialogDescription>
            Selecione os gatilhos que este node deve verificar. O fluxo seguirá pela 
            saída correspondente ao gatilho que iniciou a execução.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo ID do Node e Apelido */}
          <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />
          
          {/* Lista de triggers para selecionar */}
          <div className="space-y-2">
            <Label>Gatilhos para verificar</Label>
            <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-3">
              {triggerOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedTriggers.includes(option.type);
                
                return (
                  <div 
                    key={option.type} 
                    className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected 
                        ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleToggleTrigger(option.type)}
                  >
                    <Checkbox
                      id={`trigger-${option.type}`}
                      checked={isSelected}
                      onCheckedChange={() => handleToggleTrigger(option.type)}
                    />
                    <Icon className={`h-4 w-4 ${isSelected ? 'text-amber-600' : 'text-muted-foreground'}`} />
                    <div className="flex-1">
                      <Label 
                        htmlFor={`trigger-${option.type}`} 
                        className="cursor-pointer font-medium"
                      >
                        {option.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview das saídas */}
          {selectedTriggers.length > 0 && (
            <div className="space-y-2">
              <Label>Saídas do node</Label>
              <div className="flex flex-wrap gap-2">
                {selectedTriggers.map((trigger) => {
                  const option = triggerOptions.find(o => o.type === trigger);
                  return (
                    <Badge key={trigger} variant="outline" className="text-amber-600">
                      {option?.label || trigger}
                    </Badge>
                  );
                })}
                <Badge variant="secondary">Padrão</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                A saída "Padrão" será usada quando nenhum dos gatilhos selecionados corresponder.
              </p>
            </div>
          )}

          {selectedTriggers.length === 0 && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                ⚠️ Selecione pelo menos um gatilho para verificar.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={selectedTriggers.length === 0}
            className="bg-amber-600 hover:bg-amber-700"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
